/**
 * ValidationScoringConfigForm Component
 *
 * Formik form for creating/editing validation scoring config (per resource).
 * Fields: impact_of_record_level, impact_of_issue_level, factor_low, factor_medium, factor_high
 *
 * ADD this file to: datatool_fe-QA/src/components/Scoring/ValidationScoringConfigForm.tsx
 */
import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
  Slider,
} from "@mui/material";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestoreIcon from "@mui/icons-material/Restore";
import { validationScoringConfigFormSchema } from "../../schemas";
import {
  IValidationScoringConfig,
  ICreateValidationScoringConfig,
  IEditValidationScoringConfig,
} from "../../types/Scoring.type";
import {
  createValidationScoringConfig,
  updateValidationScoringConfig,
  getValidationScoringConfigByResource,
} from "../../services/scoringService";
import { validationScoringConfigDefaults } from "../../services/constants";
import { SLIDER_ACCENT } from "./scoreTheme";
import { toast } from "react-toastify";
import {
  getImpactSumError,
  isScoringSubmitDisabled,
} from "../../utils/scoringConfigFormValidation";

interface IValidationScoringConfigFormProps {
  resourceId: number;
  existingConfig: IValidationScoringConfig | null;
  onSaveSuccess?: () => void;
  hideCreateButton?: boolean;
  onValuesChange?: (values: any) => void;
  onValidityChange?: (isValid: boolean) => void;
  readOnly?: boolean;
}

const ValidationScoringConfigForm: React.FC<
  IValidationScoringConfigFormProps
> = ({
  resourceId,
  existingConfig,
  onSaveSuccess,
  hideCreateButton,
  onValuesChange,
  onValidityChange,
  readOnly,
}) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [currentConfig, setCurrentConfig] =
    useState<IValidationScoringConfig | null>(existingConfig);

  useEffect(() => {
    setCurrentConfig(existingConfig);
  }, [existingConfig]);

  const isEditMode =
    currentConfig !== null && currentConfig.id !== null && currentConfig.id > 0;

  const formik = useFormik({
    initialValues: {
      impact_of_record_level:
        currentConfig?.impact_of_record_level ??
        validationScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        currentConfig?.impact_of_issue_level ??
        validationScoringConfigDefaults.impact_of_issue_level,
      factor_low:
        currentConfig?.factor_low ?? validationScoringConfigDefaults.factor_low,
      factor_medium:
        currentConfig?.factor_medium ??
        validationScoringConfigDefaults.factor_medium,
      factor_high:
        currentConfig?.factor_high ??
        validationScoringConfigDefaults.factor_high,
      comment: "",
    },
    validationSchema: validationScoringConfigFormSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (isEditMode) {
          const editBody: IEditValidationScoringConfig = {
            impact_of_record_level: values.impact_of_record_level,
            impact_of_issue_level: values.impact_of_issue_level,
            factor_low: values.factor_low,
            factor_medium: values.factor_medium,
            factor_high: values.factor_high,
            comment: values.comment,
          };
          await updateValidationScoringConfig(currentConfig!.id, editBody);
          toast.success("Validation scoring config updated successfully.");
        } else {
          const createBody: ICreateValidationScoringConfig = {
            resource_id: resourceId,
            impact_of_record_level: values.impact_of_record_level,
            impact_of_issue_level: values.impact_of_issue_level,
            factor_low: values.factor_low,
            factor_medium: values.factor_medium,
            factor_high: values.factor_high,
          };
          await createValidationScoringConfig(createBody);
          toast.success("Validation scoring config created successfully.");
        }
        // Refetch the config so form switches to edit mode
        if (resourceId && resourceId > 0) {
          const refreshed = await getValidationScoringConfigByResource(
            resourceId
          );
          setCurrentConfig(refreshed);
        }
        setHasChanges(false);
        if (onSaveSuccess) onSaveSuccess();
      } catch (error) {
        console.error(error);
        toast.error("Failed to save validation scoring config.");
      }
    },
  });

  useEffect(() => {
    const initial = formik.initialValues;
    const current = formik.values;
    const changed =
      initial.impact_of_record_level !== current.impact_of_record_level ||
      initial.impact_of_issue_level !== current.impact_of_issue_level ||
      initial.factor_low !== current.factor_low ||
      initial.factor_medium !== current.factor_medium ||
      initial.factor_high !== current.factor_high;
    setHasChanges(changed);
  }, [formik.values]);

  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(formik.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  useEffect(() => {
    onValidityChange?.(formik.isValid);
  }, [formik.isValid, onValidityChange]);

  /**
   * Custom onChange / onBlur for numeric <input type="number"> fields.
   *
   * DQ-8556: when the browser reports e.target.value="" for an intermediate
   * decimal (e.g. "0."), we must NOT immediately commit "" so that the browser
   * input buffer can keep showing the partial value while the user continues
   * typing. NaN-producing non-empty strings (impossible for type="number" in
   * practice) are also left untouched.
   *
   * DQ-8521: when the user genuinely clears the field (all digits deleted,
   * e.target.value === ""), commit "" so that React re-renders with an empty
   * controlled value instead of snapping back to the previous Formik value,
   * and so that the Yup required-field validation fires on blur.
   */
  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (value === "") {
      formik.setFieldValue(name, "");
      return;
    }
    const v = parseFloat(value);
    if (!isNaN(v)) {
      formik.setFieldValue(name, v);
    }
    // Non-empty NaN (intermediate state left by the browser): leave Formik
    // untouched so the browser input buffer persists — DQ-8556 fix.
  };

  const handleNumericBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    formik.handleBlur(e);
    if (isNaN(parseFloat(e.target.value))) {
      formik.setFieldValue(e.target.name, "");
    }
  };

  const impactSumError = getImpactSumError(formik.errors);

  const handleResetToDefaults = () => {
    formik.setValues({
      impact_of_record_level:
        validationScoringConfigDefaults.impact_of_record_level,
      impact_of_issue_level:
        validationScoringConfigDefaults.impact_of_issue_level,
      factor_low: validationScoringConfigDefaults.factor_low,
      factor_medium: validationScoringConfigDefaults.factor_medium,
      factor_high: validationScoringConfigDefaults.factor_high,
      comment: formik.values.comment,
    });
  };

  return (
    <Box
      sx={{ p: 2, mt: "20px", mb: 0 }}
      className="accordion-panel text-box-card compact-text-box-card no-radius bottom-radius"
    >
      <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 700 }}>
        Validation Scoring Configuration
      </Typography>

      <Grid
        container
        rowSpacing={2.5}
        columnSpacing={{ xl: 4, lg: 3, md: 2, sm: 2, xs: 1 }}
      >
        {/* Impact of Record Level */}
        <Grid item xs={12} md={6}>
          <label className="label-text">
            Impact of Record Level:{" "}
            {(Number(formik.values.impact_of_record_level) || 0).toFixed(2)}
          </label>
          <Slider
            name="impact_of_record_level"
            value={Number(formik.values.impact_of_record_level) || 0}
            onChange={(_, val) =>
              formik.setFieldValue("impact_of_record_level", val)
            }
            disabled={readOnly}
            min={0}
            max={1}
            step={0.05}
            marks={[
              { value: 0, label: "0" },
              { value: 0.5, label: "0.5" },
              { value: 1, label: "1" },
            ]}
            valueLabelDisplay="auto"
            sx={{
              color: SLIDER_ACCENT,
              "& .MuiSlider-thumb": { color: SLIDER_ACCENT },
              "& .MuiSlider-track": { color: SLIDER_ACCENT, border: "none" },
              "& .MuiSlider-rail": { color: SLIDER_ACCENT, opacity: 0.3 },
              "& .MuiSlider-valueLabel": { backgroundColor: SLIDER_ACCENT },
              "& .MuiSlider-markActive": { backgroundColor: SLIDER_ACCENT },
            }}
          />
          {formik.touched.impact_of_record_level &&
            formik.errors.impact_of_record_level &&
            formik.errors.impact_of_record_level !== impactSumError && (
              <Typography color="error" variant="caption">
                {formik.errors.impact_of_record_level}
              </Typography>
            )}
        </Grid>

        {/* Impact of Issue Level */}
        <Grid item xs={12} md={6}>
          <label className="label-text">
            Impact of Issue Level:{" "}
            {(Number(formik.values.impact_of_issue_level) || 0).toFixed(2)}
          </label>
          <Slider
            name="impact_of_issue_level"
            value={Number(formik.values.impact_of_issue_level) || 0}
            onChange={(_, val) =>
              formik.setFieldValue("impact_of_issue_level", val)
            }
            disabled={readOnly}
            min={0}
            max={1}
            step={0.05}
            marks={[
              { value: 0, label: "0" },
              { value: 0.5, label: "0.5" },
              { value: 1, label: "1" },
            ]}
            valueLabelDisplay="auto"
            sx={{
              color: SLIDER_ACCENT,
              "& .MuiSlider-thumb": { color: SLIDER_ACCENT },
              "& .MuiSlider-track": { color: SLIDER_ACCENT, border: "none" },
              "& .MuiSlider-rail": { color: SLIDER_ACCENT, opacity: 0.3 },
              "& .MuiSlider-valueLabel": { backgroundColor: SLIDER_ACCENT },
              "& .MuiSlider-markActive": { backgroundColor: SLIDER_ACCENT },
            }}
          />
          {formik.touched.impact_of_issue_level &&
            formik.errors.impact_of_issue_level &&
            formik.errors.impact_of_issue_level !== impactSumError && (
              <Typography color="error" variant="caption">
                {formik.errors.impact_of_issue_level}
              </Typography>
            )}
        </Grid>

        {impactSumError && !readOnly && (
          <Grid item xs={12}>
            <Typography color="error" variant="body2">
              {impactSumError}
            </Typography>
          </Grid>
        )}

        {/* Factor Low */}
        <Grid item xs={12} md={4}>
          <label className="label-text">Factor (Low)</label>
          <TextField
            name="factor_low"
            type="number"
            inputProps={{ step: 0.05 }}
            value={formik.values.factor_low}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            disabled={readOnly}
            InputProps={{ readOnly: readOnly }}
            className={`form-control ${
              formik.errors.factor_low ? "has-error" : ""
            }`}
            error={Boolean(formik.errors.factor_low)}
            helperText={formik.errors.factor_low || " "}
          />
        </Grid>

        {/* Factor Medium */}
        <Grid item xs={12} md={4}>
          <label className="label-text">Factor (Medium)</label>
          <TextField
            name="factor_medium"
            type="number"
            inputProps={{ step: 0.05 }}
            value={formik.values.factor_medium}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            disabled={readOnly}
            InputProps={{ readOnly: readOnly }}
            className={`form-control ${
              formik.errors.factor_medium ? "has-error" : ""
            }`}
            error={Boolean(formik.errors.factor_medium)}
            helperText={formik.errors.factor_medium || " "}
          />
        </Grid>

        {/* Factor High */}
        <Grid item xs={12} md={4}>
          <label className="label-text">Factor (High)</label>
          <TextField
            name="factor_high"
            type="number"
            inputProps={{ step: 0.05 }}
            value={formik.values.factor_high}
            onChange={handleNumericChange}
            onBlur={handleNumericBlur}
            disabled={readOnly}
            InputProps={{ readOnly: readOnly }}
            className={`form-control ${
              formik.errors.factor_high ? "has-error" : ""
            }`}
            error={Boolean(formik.errors.factor_high)}
            helperText={formik.errors.factor_high || " "}
          />
        </Grid>

        {/* Comment (only for edit mode) */}
        {isEditMode && !readOnly && (
          <Grid item xs={12}>
            <label className="label-text">Change Comment</label>
            <TextField
              name="comment"
              value={formik.values.comment}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className="form-control"
              multiline
              rows={2}
            />
          </Grid>
        )}
      </Grid>

      {/* Action Buttons */}
      {!readOnly && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            columnGap: 1.5,
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            className="btn-orange btn-dark"
            disabled={formik.isSubmitting}
            onClick={handleResetToDefaults}
            startIcon={<RestoreIcon />}
          >
            Reset to Defaults
          </Button>
          {!hideCreateButton && (
            <Button
              variant="contained"
              className="btn-orange"
              disabled={isScoringSubmitDisabled({
                isSubmitting: formik.isSubmitting,
                isValid: formik.isValid,
                isEditMode,
                hasChanges,
              })}
              startIcon={
                formik.isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SaveOutlinedIcon />
                )
              }
              onClick={() => formik.handleSubmit()}
            >
              {formik.isSubmitting ? "Saving..." : "Save"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ValidationScoringConfigForm;
